/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { transformViewToRotatedView } from "dc-preview-utils/src/Utils/PageUtils";
import { isFirefox, isSafari } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getPageZoomLevel } from "selectors/zoom";
import {
  getPageWidth,
  getPageHeight,
  getPageHandle,
  getPageRotation,
  getOrigPageRotation
} from "selectors/pageSelectors";
import {
  isReadAloudActive,
  isReadAloudPaused,
  isReadAloudPausedByButton,
  getPagesText,
  getPageIndex,
  getNodeIndex,
  getWordPosition,
  isLastNode,
  getIsScrolling
} from "selectors/readAloudSelectors";
import { isContainerNode } from "utils/AccessibilityUtils";
import { debounce } from "utils/lodashUtils";
import { SAFARI_FIREFOX_READALOUD_DEBOUNCE_WAIT } from "constants/constants";
import { getViewPortHeight, getViewPortWidth } from "selectors/documentViewSelectors";
import { getPagesViewYPos, getTotalHeightTillPageTop, getYPosition, getXPosition } from "selectors/pagedViewState";
import { getCurrentViewState, getCurrentPageIndex } from "selectors/pageNumber";
import { isFullScreenOrFitOnePageMode, getViewRotationAngle } from "selectors/documentSelectors";

const isSpecialCharacter = c => {
  const specialCharacters = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
  return c.length === 1 && specialCharacters.test(c);
};

export const getPositionFromRect = (rect, pageIndex, stores, zoomLevel) => {
  if (!rect) return {};

  const scale = zoomLevel || getPageZoomLevel(stores, pageIndex);
  let { xMax, xMin, yMax, yMin } = rect;

  [xMax, xMin, yMax, yMin] = [xMax, xMin, yMax, yMin].map(coord => coord * scale);
  const viewRotation = getViewRotationAngle(stores);
  const currpageRotation = getPageRotation(stores, pageIndex);
  const origPageRotation = getOrigPageRotation(stores, pageIndex);

  const rotation = (viewRotation + currpageRotation - origPageRotation + 360) % 360;
  ({ x: xMin, y: yMin } = transformViewToRotatedView(xMin, yMin, pageIndex, stores, scale, rotation));
  ({ x: xMax, y: yMax } = transformViewToRotatedView(xMax, yMax, pageIndex, stores, scale, rotation));

  const position = {
    top: Math.min(yMin, yMax),
    bottom: Math.max(yMin, yMax),
    left: Math.min(xMin, xMax),
    right: Math.max(xMin, xMax)
  };

  // Taking intersection with page rects to handle invalid rects coming from T5
  position.top = Math.max(0, position.top);
  position.left = Math.max(0, position.left);
  position.bottom = Math.min(getPageHeight(stores, pageIndex), position.bottom);
  position.right = Math.min(getPageWidth(stores, pageIndex), position.right);

  return position;
};

const flattenContainerNode = node => {
  if (!node.children || node.children.length === 0) return [];
  const flattenedNodeList = [];
  node.children.forEach(child => flattenedNodeList.push(...flattenNode(child)));
  return flattenedNodeList;
};

export const flattenNode = node => (isContainerNode(node) ? flattenContainerNode(node) : [node]);

export class SpeechSynthesisWrapper {
  constructor(stores) {
    this.synth = window.speechSynthesis;
    this.stores = stores;
    this.startPosition = 0;
    this.isFiringBoundaryEvt = false;
  }

  init() {
    this.initUtterance();
    this.initVoices();
  }

  resetPosition() {
    this.setPageIndex(0);
    this.setNodeIndex(0);
    this.setWordPosition(0, 0);
    this.startPosition = 0;
  }

  getIsPaused() {
    return isReadAloudPaused(this.stores);
  }

  getIsPausedByButton() {
    return isReadAloudPausedByButton(this.stores);
  }

  getIsLastNode() {
    return isLastNode(this.stores);
  }

  getPagesText() {
    return getPagesText(this.stores);
  }

  getPageIndex() {
    return getPageIndex(this.stores);
  }

  getNodeIndex() {
    return getNodeIndex(this.stores);
  }

  getWordPosition() {
    return getWordPosition(this.stores);
  }

  getIsScrolling() {
    return getIsScrolling(this.stores);
  }

  setIsPaused(isPaused) {
    this.stores.readAloudStore.setIsPaused(isPaused);
  }

  setIsPausedByButton(isPausedByButton) {
    this.stores.readAloudStore.setIsPausedByButton(isPausedByButton);
  }

  setPageIndex(index) {
    this.stores.readAloudStore.setPageIndex(index);
  }

  setNodeIndex(index) {
    this.stores.readAloudStore.setNodeIndex(index);
  }

  setWordPosition(start, end) {
    this.stores.readAloudStore.setWordPosition(start, end);
  }

  setVoiceList(voiceList) {
    this.stores.readAloudStore.setVoiceList(voiceList);
    this.stores.readAloudStore.manageDefaultVoiceSelection();
  }

  initVoices() {
    const populateVoices = () => {
      const voiceList = this.synth.getVoices();
      this.setVoiceList(voiceList);
    };
    // call in case voices are already available
    populateVoices();
    // event fires when voices are available
    this.synth.onvoiceschanged = populateVoices;
  }

  initUtterance() {
    this.utterance = new SpeechSynthesisUtterance();

    // For some voices (Google voices, for eg), the word boundary event does not fire. We will highlight whole node in this case
    this.utterance.onstart = () => this.stores.readAloudStore.handleMissingBoundaryEvent();
  }

  unsetBoundaryHandler() {
    this.utterance.onboundary = undefined;
  }

  unsetEndHandler() {
    this.utterance.onend = undefined;
  }

  setBoundaryHandler() {
    this.utterance.onboundary = e => {
      this.isFiringBoundaryEvt = true;
      const currentText = this.getCurrentNodeText();
      // charLength is not a standard and not supported on Safari, hence manually find boundary if needed.
      const charLength = e.charLength ? e.charLength : currentText.slice(e.charIndex).search(/\w\b/) + 1;
      if (this.startPosition + e.charIndex + charLength <= currentText.length) {
        const start = this.startPosition + e.charIndex;
        const end = this.startPosition + e.charIndex + charLength;
        this.setWordPosition(start, end);
      }
    };
  }

  setEndHandler() {
    this.utterance.onend = () => {
      if (!this.getIsPaused()) {
        if (this.getIsLastNode()) {
          this.pauseByButton();
          this.stores.readAloudStore.shouldResetReadAloud = true;
          return;
        }
        this.startPosition = 0;
        this.setWordPosition(0, 0);
        this.goToNextNode();
        this.play();
      }
    };
  }

  setVoice(voice) {
    this.pause();
    this.utterance.voice = voice;
    if (isReadAloudActive(this.stores) && !this.getIsPausedByButton()) this.play();
    this.isFiringBoundaryEvt = false;
  }

  setRate(rate) {
    this.pause();
    this.utterance.rate = rate;
    if (isReadAloudActive(this.stores) && !this.getIsPausedByButton()) this.play();
  }

  getCurrentNodeText() {
    const pagesText = this.getPagesText();
    const node = pagesText[this.getPageIndex()][this.getNodeIndex()];
    return node.contentDesc;
  }

  speak() {
    // speak from this.startPosition,
    this.utterance.text = this.getCurrentNodeText().slice(this.startPosition);
    this.setBoundaryHandler();
    this.setEndHandler();
    this.synth.speak(this.utterance);
  }

  debouncedSpeak = debounce(() => !this.getIsScrolling() && this.speak(), SAFARI_FIREFOX_READALOUD_DEBOUNCE_WAIT);

  pause() {
    this.setIsPaused(true);
    this.startPosition = this.getWordPosition().start;
    this.unsetBoundaryHandler();
    this.unsetEndHandler();
    this.synth.cancel();
  }

  pauseByButton() {
    this.pause();
    this.setIsPausedByButton(true);
  }

  play() {
    this.setIsPaused(false);
    this.setIsPausedByButton(false);
    // Safari needs some time as cancel() throws an error, and boundary does not fire immediately for subsequent text
    // Firefox call onend on calling cancel(), and onboundary may be also in the callback queue. Wait for those to clear
    if (isSafari() || isFirefox()) this.debouncedSpeak();
    else this.speak();
  }

  next() {
    this.pause();
    this.startPosition = 0;
    this.setWordPosition(0, 0);
    this.goToNextNode();
    this.play();
  }

  prev() {
    this.pause();
    this.startPosition = 0;
    this.setWordPosition(0, 0);
    this.goToPrevNode();
    this.play();
  }

  isLastNodeInPage() {
    return this.getNodeIndex() === this.getPagesText()[this.getPageIndex()].length - 1;
  }

  isFirstNodeInPage() {
    return this.getNodeIndex() === 0;
  }

  goToNextNode() {
    const pagesText = this.getPagesText(),
      nodeIndex = this.getNodeIndex(),
      pageIndex = this.getPageIndex();
    if (this.isLastNodeInPage()) {
      let i = pageIndex + 1;
      while (i < pagesText.length && pagesText[i].length === 0) i++;
      this.setNodeIndex(0);
      this.setPageIndex(i);
    } else this.setNodeIndex(nodeIndex + 1);
    if (isSpecialCharacter(this.getCurrentNodeText())) return this.goToNextNode();
    return true;
  }

  goToPrevNode() {
    const pagesText = this.getPagesText(),
      nodeIndex = this.getNodeIndex(),
      pageIndex = this.getPageIndex();
    if (this.isFirstNodeInPage()) {
      let i = pageIndex - 1;
      while (i >= 0 && pagesText[i].length === 0) i--;
      this.setPageIndex(i);
      this.setNodeIndex(pagesText[i].length - 1);
    } else this.setNodeIndex(nodeIndex - 1);
    if (isSpecialCharacter(this.getCurrentNodeText())) return this.goToPrevNode();
    return true;
  }
}

export const isRectInView = (stores, pageIndex, rect) => {
  let isInViewY, isInViewX;
  if (isFullScreenOrFitOnePageMode(stores)) {
    isInViewY = getCurrentPageIndex(stores) === pageIndex;
    isInViewX = getCurrentPageIndex(stores) === pageIndex;
  } else {
    const pageTopHeight = getTotalHeightTillPageTop(stores, pageIndex),
      pageHandle = getPageHandle(stores, pageIndex),
      { top, bottom, left: rectLeft, right: rectRight } = getPositionFromRect(rect, pageIndex, stores),
      rectTop = Math.ceil(top + pageTopHeight),
      rectBottom = bottom + pageTopHeight,
      scrolledHeight = getPagesViewYPos(stores),
      scrolledWidth = pageHandle && pageHandle.getBoundingClientRect().left;
    isInViewY = rectTop >= scrolledHeight && rectBottom <= scrolledHeight + getViewPortHeight(stores);
    isInViewX = rectLeft + scrolledWidth >= 0 && rectRight + scrolledWidth <= getViewPortWidth(stores);
  }
  return { isInViewY, isInViewX };
};

export const setRectInView = (stores, pageIndex, rect, scrollCallback) => {
  const { isInViewY, isInViewX } = isRectInView(stores, pageIndex, rect);
  if (!(isInViewX && isInViewY)) {
    if (scrollCallback && scrollCallback instanceof Function) scrollCallback();
    const { currentPageIndex, xInPage, yInPage } = getCurrentViewState(stores);
    const { top, left } = getPositionFromRect(rect, pageIndex, stores, 1);
    const newPageIndex = isInViewY ? currentPageIndex : pageIndex;
    const newYPos = getYPosition(stores, newPageIndex, isInViewY ? yInPage : top);
    const newXPos = getXPosition(stores, newPageIndex, isInViewX ? xInPage : left);
    // pagedViewState.goToLocation does not consider rotated view for validating arguments
    if (isFullScreenOrFitOnePageMode(stores)) {
      stores.pagedViewState.goToPage(newPageIndex + 1);
    } else {
      stores.pagedViewState.scrollPagesView(newXPos, newYPos);
    }
  }
};
